import { useSearchParams } from 'react-router'

export interface UseListSortOptions {
  defaultSort: string
  defaultOrder: 'asc' | 'desc'
}

export interface UseListSortResult {
  sort: string
  order: 'asc' | 'desc'
  setSort: (key: string) => void
  toggleOrder: () => void
  sortParams: { sort?: string; sort_order?: string }
}

/**
 * EP-97: URL-param-backed sort state for list pages.
 *
 * - Reads ?sort= and ?sort_order= from the URL on mount (deep-link / back-button).
 * - When resolved sort+order equal the page defaults, REMOVES the params to keep
 *   a clean canonical URL.
 * - setSort(key): toggling the same key flips asc↔desc; a new key resets to asc.
 * - toggleOrder(): flips the current order for the active sort key.
 * - sortParams: spreads into TanStack Query queryKey / API params (omits keys when
 *   at defaults so the request URL stays canonical too).
 * - Preserves ALL other existing search params (status, period, from, to, tab, page…).
 */
export function useListSort({ defaultSort, defaultOrder }: UseListSortOptions): UseListSortResult {
  const [searchParams, setSearchParams] = useSearchParams()

  const rawSort = searchParams.get('sort')
  const rawOrder = searchParams.get('sort_order')

  const sort = rawSort ?? defaultSort
  const order: 'asc' | 'desc' =
    rawOrder === 'asc' || rawOrder === 'desc' ? rawOrder : defaultOrder

  const isDefault = sort === defaultSort && order === defaultOrder

  const sortParams: { sort?: string; sort_order?: string } = isDefault
    ? {}
    : { sort, sort_order: order }

  const applySort = (nextSort: string, nextOrder: 'asc' | 'desc') => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (nextSort === defaultSort && nextOrder === defaultOrder) {
          // Return to canonical URL — remove the params.
          next.delete('sort')
          next.delete('sort_order')
        } else {
          next.set('sort', nextSort)
          next.set('sort_order', nextOrder)
        }
        return next
      },
      { replace: true },
    )
  }

  const setSort = (key: string) => {
    if (key === sort) {
      // Same column — toggle order.
      const nextOrder = order === 'asc' ? 'desc' : 'asc'
      applySort(sort, nextOrder)
    } else {
      // New column — always start ascending.
      applySort(key, 'asc')
    }
  }

  const toggleOrder = () => {
    const nextOrder = order === 'asc' ? 'desc' : 'asc'
    applySort(sort, nextOrder)
  }

  return { sort, order, setSort, toggleOrder, sortParams }
}
